import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Download, Printer, FileBarChart2, Building2, Users, CalendarDays, Clock, Receipt, Search, Eye } from 'lucide-react';

import { currentColomboMonthYear, formatDateTime } from '../lib/datetime';
import { ReceiptModal } from '../components/ReceiptModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatLKR = (n) => `LKR ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Tiny CSV helper — escapes cells with commas / quotes.
const downloadCsv = (filename, rows) => {
  const csv = rows.map((row) =>
    row.map((cell) => {
      const s = String(cell ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')
  ).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  toast.success(`Downloaded ${filename}`);
};

const TAB_MONTHLY_BILLING = 'monthly-billing';
const TAB_OUTSTANDING_PROPERTY = 'outstanding-property';
const TAB_OUTSTANDING_RESIDENT = 'outstanding-resident';
const TAB_YEARLY_MATRIX = 'yearly-matrix';
const TAB_AGING = 'aging-analysis';
const TAB_PAYMENT_REGISTER = 'payment-register';

export const Reports = () => {
  const nowLK = currentColomboMonthYear();
  const [activeTab, setActiveTab] = useState(TAB_OUTSTANDING_PROPERTY);
  const [filters, setFilters] = useState({
    period_month: nowLK.month,
    period_year: nowLK.year,
    property_id: '',
    // For outstanding tabs, allow "All months" (period_month = 0 = omit filter)
    scope: 'month',   // 'month' | 'year'
    aging_group_by: 'property',   // 'property' | 'resident' (Aging tab)
    resident_query: '',  // Payment register tab: free-text search on resident/unit/receipt
  });
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [openReceiptId, setOpenReceiptId] = useState(null);

  const loadProperties = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/properties`, { withCredentials: true });
      data.sort((a, b) => {
        const aNum = parseInt((a.name.match(/\d+/) || [0])[0], 10);
        const bNum = parseInt((b.name.match(/\d+/) || [0])[0], 10);
        return aNum - bNum;
      });
      setProperties(data);
    } catch (err) {
      console.error('Reports: failed to load properties', err);
    }
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setReportData(null);
    try {
      const params = { period_year: filters.period_year };
      if (filters.property_id) params.property_id = filters.property_id;
      if (activeTab === TAB_MONTHLY_BILLING) {
        params.period_month = filters.period_month;
        const { data } = await axios.get(`${API}/reports/monthly`, { params, withCredentials: true });
        setReportData(data);
      } else if (activeTab === TAB_OUTSTANDING_PROPERTY) {
        if (filters.scope === 'month') params.period_month = filters.period_month;
        const { data } = await axios.get(`${API}/reports/outstanding-by-property`, { params, withCredentials: true });
        setReportData(data);
      } else if (activeTab === TAB_OUTSTANDING_RESIDENT) {
        if (filters.scope === 'month') params.period_month = filters.period_month;
        const { data } = await axios.get(`${API}/reports/outstanding-by-resident`, { params, withCredentials: true });
        setReportData(data);
      } else if (activeTab === TAB_YEARLY_MATRIX) {
        const { data } = await axios.get(`${API}/reports/outstanding-yearly-matrix`, { params, withCredentials: true });
        setReportData(data);
      } else if (activeTab === TAB_AGING) {
        const p = {};
        if (filters.property_id) p.property_id = filters.property_id;
        p.group_by = filters.aging_group_by;
        const { data } = await axios.get(`${API}/reports/aging-analysis`, { params: p, withCredentials: true });
        setReportData(data);
      } else if (activeTab === TAB_PAYMENT_REGISTER) {
        params.period_month = filters.period_month;
        const { data } = await axios.get(`${API}/reports/payment-register`, { params, withCredentials: true });
        setReportData(data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters.period_year, filters.period_month, filters.property_id, filters.scope, filters.aging_group_by]);

  // Load properties once + auto-refresh report when tab or filter changes.
  // Clearing reportData when the active tab changes prevents a render pass
  // where the new tab's component receives the previous tab's data shape
  // (which would crash e.g. YearlyMatrixTable expecting `r.months`).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadProperties(); }, []);
  useEffect(() => { setReportData(null); }, [activeTab]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const propertyLabel = useMemo(() => {
    if (!filters.property_id) return 'All properties';
    const p = properties.find(x => x._id === filters.property_id);
    return p?.name || filters.property_id;
  }, [filters.property_id, properties]);

  const periodLabel = filters.scope === 'year' || activeTab === TAB_YEARLY_MATRIX
    ? `${filters.period_year}`
    : `${MONTH_NAMES[filters.period_month - 1]} ${filters.period_year}`;

  // Client-side text filter for Payment Register (backend already handles
  // month / property / resident_id, but a free-text search across receipt
  // number, resident name, and unit is much friendlier than a resident
  // dropdown with 200+ names).
  const filteredPaymentRegisterRows = useMemo(() => {
    if (activeTab !== TAB_PAYMENT_REGISTER) return [];
    const rows = reportData?.rows || [];
    const q = (filters.resident_query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.resident_name || '').toLowerCase().includes(q) ||
      (r.unit_number || '').toString().toLowerCase().includes(q) ||
      (r.receipt_number || '').toLowerCase().includes(q) ||
      (r.method_label || '').toLowerCase().includes(q) ||
      (r.applied_invoice_numbers || []).some((n) => (n || '').toLowerCase().includes(q))
    );
  }, [activeTab, reportData, filters.resident_query]);

  // ---------- CSV exports per tab ----------
  const exportCurrentCSV = () => {
    if (!reportData) {
      toast.error('Nothing to export');
      return;
    }
    const safeProp = filters.property_id ? '-' + propertyLabel.replace(/\s+/g, '_') : '';

    if (activeTab === TAB_MONTHLY_BILLING) {
      const rows = reportData.rows || [];
      if (!rows.length) return toast.error('Nothing to export');
      const headers = ['Resident','Unit','Property','Maintenance Billed','Maintenance Paid','Maint Status','Water Billed','Water Paid','Water Status','Total Billed','Total Paid','Outstanding'];
      const data = rows.map(r => [
        r.resident_name, r.unit_number || '', r.property_name,
        r.maintenance_amount.toFixed(2), r.maintenance_paid.toFixed(2), r.maintenance_status || '',
        r.water_amount.toFixed(2), r.water_paid.toFixed(2), r.water_status || '',
        r.total_amount.toFixed(2), r.total_paid.toFixed(2), r.total_outstanding.toFixed(2),
      ]);
      downloadCsv(`monthly-billing-${periodLabel.replace(' ', '_')}${safeProp}.csv`, [headers, ...data]);
    } else if (activeTab === TAB_OUTSTANDING_PROPERTY) {
      const rows = reportData.rows || [];
      if (!rows.length) return toast.error('Nothing to export');
      const headers = ['Property','Maintenance','Water','Common Water','Common Electricity','Total Outstanding','Invoice Count'];
      const data = rows.map(r => [
        r.property_name,
        r.maintenance_outstanding.toFixed(2), r.water_outstanding.toFixed(2),
        r.common_water_outstanding.toFixed(2), r.common_electricity_outstanding.toFixed(2),
        r.total_outstanding.toFixed(2), r.invoice_count,
      ]);
      const t = reportData.totals || {};
      data.push([
        'TOTAL',
        (t.maintenance_outstanding || 0).toFixed(2), (t.water_outstanding || 0).toFixed(2),
        (t.common_water_outstanding || 0).toFixed(2), (t.common_electricity_outstanding || 0).toFixed(2),
        (t.total_outstanding || 0).toFixed(2), '',
      ]);
      downloadCsv(`outstanding-by-property-${periodLabel.replace(' ', '_')}${safeProp}.csv`, [headers, ...data]);
    } else if (activeTab === TAB_OUTSTANDING_RESIDENT) {
      const rows = reportData.rows || [];
      if (!rows.length) return toast.error('Nothing to export');
      const headers = ['Resident','Unit','Property','Maintenance','Water','Common Water','Common Electricity','Total Outstanding','Invoice Count'];
      const data = rows.map(r => [
        r.resident_name, r.unit_number || '', r.property_name,
        r.maintenance_outstanding.toFixed(2), r.water_outstanding.toFixed(2),
        r.common_water_outstanding.toFixed(2), r.common_electricity_outstanding.toFixed(2),
        r.total_outstanding.toFixed(2), r.invoice_count,
      ]);
      downloadCsv(`outstanding-by-resident-${periodLabel.replace(' ', '_')}${safeProp}.csv`, [headers, ...data]);
    } else if (activeTab === TAB_YEARLY_MATRIX) {
      const rows = (reportData.rows || []).filter(r => Array.isArray(r?.months) && r.months.length === 12);
      if (!rows.length) return toast.error('Nothing to export');
      const headers = ['Property', ...MONTH_NAMES, 'Year Total'];
      const data = rows.map(r => [r.property_name, ...r.months.map(v => v.toFixed(2)), r.year_total.toFixed(2)]);
      const mt = reportData.monthly_totals || [];
      data.push(['TOTAL', ...mt.map(v => v.toFixed(2)), (reportData.year_total || 0).toFixed(2)]);
      downloadCsv(`outstanding-yearly-matrix-${filters.period_year}${safeProp}.csv`, [headers, ...data]);
    } else if (activeTab === TAB_AGING) {
      const rows = reportData.rows || [];
      if (!rows.length) return toast.error('Nothing to export');
      const groupCol = reportData.group_by === 'resident' ? 'Resident' : 'Property';
      const headers = [groupCol, 'Unit', 'Property', 'Not Due', '0-30 days', '31-60 days', '61-90 days', '90+ days', 'Total Outstanding', 'Oldest (days)'];
      const b = (r, k) => r.buckets[k].outstanding.toFixed(2);
      const data = rows.map(r => [
        reportData.group_by === 'resident' ? r.resident_name : r.property_name,
        reportData.group_by === 'resident' ? (r.unit_number || '') : '',
        reportData.group_by === 'resident' ? r.property_name : '',
        b(r, 'not_due'), b(r, 'd_0_30'), b(r, 'd_31_60'), b(r, 'd_61_90'), b(r, 'd_90_plus'),
        r.total_outstanding.toFixed(2), r.oldest_days_overdue,
      ]);
      const t = reportData.totals || {};
      data.push([
        'TOTAL', '', '',
        (t.not_due?.outstanding || 0).toFixed(2), (t.d_0_30?.outstanding || 0).toFixed(2),
        (t.d_31_60?.outstanding || 0).toFixed(2), (t.d_61_90?.outstanding || 0).toFixed(2),
        (t.d_90_plus?.outstanding || 0).toFixed(2),
        (reportData.grand_total || 0).toFixed(2), '',
      ]);
      downloadCsv(`aging-${reportData.group_by}${safeProp}.csv`, [headers, ...data]);
    } else if (activeTab === TAB_PAYMENT_REGISTER) {
      const rows = filteredPaymentRegisterRows;
      if (!rows.length) return toast.error('Nothing to export');
      const headers = ['Payment Date','Receipt #','Resident','Unit','Property','Method','Reference','Amount (LKR)','Applied Invoices','Overpayment (Credit)','Recorded By'];
      const data = rows.map(r => [
        r.payment_date ? formatDateTime(r.payment_date) : '',
        r.receipt_number || '',
        r.resident_name || '',
        r.unit_number || '',
        r.property_name || '',
        r.method_label || r.method || '',
        r.note || '',
        r.amount.toFixed(2),
        (r.applied_invoice_numbers || []).join(' + '),
        (r.cascade_overpayment || 0).toFixed(2),
        r.recorded_by || '',
      ]);
      const totalAmt = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
      data.push(['TOTAL','','','','','','', totalAmt.toFixed(2), '', '', '']);
      downloadCsv(`payment-register-${periodLabel.replace(' ', '_')}${safeProp}.csv`, [headers, ...data]);
    }
  };

  const tabs = [
    { id: TAB_OUTSTANDING_PROPERTY, label: 'Outstanding · Property', icon: Building2 },
    { id: TAB_OUTSTANDING_RESIDENT, label: 'Outstanding · Resident', icon: Users },
    { id: TAB_AGING,                label: 'Aging Analysis',         icon: Clock },
    { id: TAB_YEARLY_MATRIX,       label: 'Yearly Matrix',          icon: CalendarDays },
    { id: TAB_MONTHLY_BILLING,     label: 'Monthly Billing',        icon: FileBarChart2 },
    { id: TAB_PAYMENT_REGISTER,    label: 'Payment Register',       icon: Receipt },
  ];

  const showMonthPicker = activeTab === TAB_MONTHLY_BILLING
    || activeTab === TAB_PAYMENT_REGISTER
    || ((activeTab === TAB_OUTSTANDING_PROPERTY || activeTab === TAB_OUTSTANDING_RESIDENT) && filters.scope === 'month');
  const showScopeToggle = activeTab === TAB_OUTSTANDING_PROPERTY || activeTab === TAB_OUTSTANDING_RESIDENT;
  const showYearPicker = activeTab !== TAB_AGING;
  const showAgingGroupBy = activeTab === TAB_AGING;
  const showResidentSearch = activeTab === TAB_PAYMENT_REGISTER;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Reports</h1>
          <p className="text-sm text-[#64748b]">
            Outstanding balances and collections analytics — {periodLabel} · {propertyLabel}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} className="border-[#e2e8f0]" data-testid="print-report-button">
            <Printer className="mr-2 h-4 w-4" /> Print
          </Button>
          <Button onClick={exportCurrentCSV} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="export-csv-button">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-[#e2e8f0]" data-testid="reports-tabs">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm font-medium inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                active ? 'border-[#2563eb] text-[#2563eb]' : 'border-transparent text-[#64748b] hover:text-[#0f172a]'
              }`}
              data-testid={`reports-tab-${t.id}`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-4 bg-white border border-[#e2e8f0]">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {showScopeToggle && (
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Scope</Label>
              <select
                value={filters.scope}
                onChange={(e) => setFilters({ ...filters, scope: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                data-testid="reports-scope-select"
              >
                <option value="month">Single month</option>
                <option value="year">Whole year</option>
              </select>
            </div>
          )}
          {showAgingGroupBy && (
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Group by</Label>
              <select
                value={filters.aging_group_by}
                onChange={(e) => setFilters({ ...filters, aging_group_by: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                data-testid="reports-aging-groupby-select"
              >
                <option value="property">Property</option>
                <option value="resident">Resident</option>
              </select>
            </div>
          )}
          {showMonthPicker && (
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Month</Label>
              <select
                value={filters.period_month}
                onChange={(e) => setFilters({ ...filters, period_month: parseInt(e.target.value, 10) })}
                className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                data-testid="reports-month-select"
              >
                {MONTH_NAMES.map((m, i) => <option key={m} value={i+1}>{m}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Year</Label>
            <Input
              type="number" min={2020} max={2100}
              value={filters.period_year}
              onChange={(e) => setFilters({ ...filters, period_year: parseInt(e.target.value, 10) })}
              disabled={!showYearPicker}
              data-testid="reports-year-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</Label>
            <select
              value={filters.property_id}
              onChange={(e) => setFilters({ ...filters, property_id: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
              data-testid="reports-property-select"
            >
              <option value="">All properties</option>
              {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>
          {showResidentSearch && (
            <div className="space-y-1 md:col-span-1">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Search resident / receipt</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
                <Input
                  value={filters.resident_query}
                  onChange={(e) => setFilters({ ...filters, resident_query: e.target.value })}
                  placeholder="Name, unit, RCP-…"
                  className="pl-9 h-10"
                  data-testid="reports-resident-search"
                />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Body */}
      {loading ? (
        <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto mb-3" />
          <p className="text-sm text-[#64748b]">Loading report…</p>
        </Card>
      ) : !reportData ? null : activeTab === TAB_OUTSTANDING_PROPERTY ? (
        <OutstandingPropertyTable data={reportData} periodLabel={periodLabel} />
      ) : activeTab === TAB_OUTSTANDING_RESIDENT ? (
        <OutstandingResidentTable data={reportData} periodLabel={periodLabel} />
      ) : activeTab === TAB_YEARLY_MATRIX ? (
        <YearlyMatrixTable data={reportData} year={filters.period_year} />
      ) : activeTab === TAB_AGING ? (
        <AgingTable data={reportData} />
      ) : activeTab === TAB_PAYMENT_REGISTER ? (
        <PaymentRegisterTable
          data={reportData}
          rows={filteredPaymentRegisterRows}
          periodLabel={periodLabel}
          onOpenReceipt={setOpenReceiptId}
          hasQuery={Boolean((filters.resident_query || '').trim())}
        />
      ) : (
        <MonthlyBillingTable data={reportData} />
      )}
      {openReceiptId && (
        <ReceiptModal
          submissionId={openReceiptId}
          onClose={() => setOpenReceiptId(null)}
        />
      )}
    </div>
  );
};

// ---------- Outstanding by Property ----------
const OutstandingPropertyTable = ({ data, periodLabel }) => {
  const rows = data?.rows || [];
  const t = data?.totals || {};
  if (rows.length === 0) {
    return <Card className="p-8 text-center bg-white border border-[#e2e8f0]" data-testid="outstanding-property-empty">
      <p className="text-sm text-[#64748b]">Nothing outstanding for {periodLabel}. 🎉</p>
    </Card>;
  }
  return (
    <Card className="p-4 bg-white border border-[#e2e8f0] overflow-x-auto" data-testid="outstanding-property-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[#e2e8f0]">
            <th className="py-2 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Maintenance</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Water</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Common Water</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Common Elec.</th>
            <th className="py-2 pl-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.property_id || r.property_name} className="border-b border-[#f1f5f9]" data-testid={`op-row-${r.property_id || r.property_name}`}>
              <td className="py-2 pr-2">
                <p className="font-medium text-[#0f172a]">{r.property_name}</p>
                <p className="text-xs text-[#64748b]">{r.invoice_count} inv</p>
              </td>
              <td className="py-2 px-2 text-right">{formatLKR(r.maintenance_outstanding)}</td>
              <td className="py-2 px-2 text-right">{formatLKR(r.water_outstanding)}</td>
              <td className="py-2 px-2 text-right">{formatLKR(r.common_water_outstanding)}</td>
              <td className="py-2 px-2 text-right">{formatLKR(r.common_electricity_outstanding)}</td>
              <td className="py-2 pl-2 text-right font-semibold text-[#ef4444]">{formatLKR(r.total_outstanding)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-[#e2e8f0]" data-testid="op-total-row">
            <td className="py-3 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Total</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.maintenance_outstanding)}</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.water_outstanding)}</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.common_water_outstanding)}</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.common_electricity_outstanding)}</td>
            <td className="py-3 pl-2 text-right text-base font-semibold text-[#ef4444]">{formatLKR(t.total_outstanding)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

// ---------- Outstanding by Resident ----------
const OutstandingResidentTable = ({ data, periodLabel }) => {
  const rows = data?.rows || [];
  const t = data?.totals || {};
  if (rows.length === 0) {
    return <Card className="p-8 text-center bg-white border border-[#e2e8f0]" data-testid="outstanding-resident-empty">
      <p className="text-sm text-[#64748b]">No resident has an outstanding balance for {periodLabel}. 🎉</p>
    </Card>;
  }
  return (
    <Card className="p-4 bg-white border border-[#e2e8f0] overflow-x-auto" data-testid="outstanding-resident-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[#e2e8f0]">
            <th className="py-2 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Resident</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Maintenance</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Water</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Common Water</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Common Elec.</th>
            <th className="py-2 pl-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.resident_id ? `${r.resident_id}-${r.unit_number || ''}-${r.property_id || ''}` : r.resident_name} className="border-b border-[#f1f5f9]" data-testid={`or-row-${r.resident_id || r.resident_name}`}>
              <td className="py-2 pr-2">
                <p className="font-medium text-[#0f172a]">{r.resident_name}</p>
                <p className="text-xs text-[#64748b]">{r.unit_number ? `Unit ${r.unit_number} · ` : ''}{r.property_name} · {r.invoice_count} inv</p>
              </td>
              <td className="py-2 px-2 text-right">{formatLKR(r.maintenance_outstanding)}</td>
              <td className="py-2 px-2 text-right">{formatLKR(r.water_outstanding)}</td>
              <td className="py-2 px-2 text-right">{formatLKR(r.common_water_outstanding)}</td>
              <td className="py-2 px-2 text-right">{formatLKR(r.common_electricity_outstanding)}</td>
              <td className="py-2 pl-2 text-right font-semibold text-[#ef4444]">{formatLKR(r.total_outstanding)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-[#e2e8f0]" data-testid="or-total-row">
            <td className="py-3 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Total</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.maintenance_outstanding)}</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.water_outstanding)}</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.common_water_outstanding)}</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.common_electricity_outstanding)}</td>
            <td className="py-3 pl-2 text-right text-base font-semibold text-[#ef4444]">{formatLKR(t.total_outstanding)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

// ---------- Yearly Matrix (property × 12 months) ----------
const YearlyMatrixTable = ({ data, year }) => {
  const rows = data?.rows || [];
  const monthlyTotals = data?.monthly_totals || Array(12).fill(0);
  const yearTotal = data?.year_total || 0;
  // Guard against a tab-switch race: for a split second after the user
  // switches tabs, `data` is still the previous tab's report (e.g. aging,
  // outstanding-by-property) whose rows lack a `months[]` array. Only accept
  // rows that look like yearly-matrix rows to prevent a "r.months.map"
  // TypeError before the fresh fetch resolves.
  const matrixRows = rows.filter(r => Array.isArray(r?.months) && r.months.length === 12);
  if (matrixRows.length === 0) {
    return <Card className="p-8 text-center bg-white border border-[#e2e8f0]" data-testid="yearly-matrix-empty">
      <p className="text-sm font-semibold text-[#0f172a]">No outstanding balances for the {year} billing period. 🎉</p>
      <p className="text-xs text-[#64748b] mt-2">
        This matrix only shows invoices whose <span className="font-mono bg-[#f1f5f9] px-1 rounded">period_year</span> is {year}.
        If you have arrears from other years, change the Year filter above to see them.
      </p>
    </Card>;
  }
  return (
    <Card className="p-4 bg-white border border-[#e2e8f0] overflow-x-auto" data-testid="yearly-matrix-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left border-b border-[#e2e8f0]">
            <th className="py-2 pr-2 font-semibold uppercase tracking-widest text-[#64748b] sticky left-0 bg-white">Property</th>
            {MONTH_NAMES.map((m) => (
              <th key={m} className="py-2 px-1 font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">{m}</th>
            ))}
            <th className="py-2 pl-2 font-semibold uppercase tracking-widest text-[#64748b] text-right">Year Total</th>
          </tr>
        </thead>
        <tbody>
          {matrixRows.map((r) => (
            <tr key={r.property_id || r.property_name} className="border-b border-[#f1f5f9]" data-testid={`ym-row-${r.property_id || r.property_name}`}>
              <td className="py-2 pr-2 font-medium text-[#0f172a] sticky left-0 bg-white truncate max-w-[220px]">{r.property_name}</td>
              {r.months.map((v, i) => (
                <td key={i} className={`py-2 px-1 text-right ${v > 0 ? 'text-[#0f172a]' : 'text-[#cbd5e1]'}`}>
                  {v > 0 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
                </td>
              ))}
              <td className="py-2 pl-2 text-right font-semibold text-[#ef4444]">{formatLKR(r.year_total)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-[#e2e8f0]" data-testid="ym-total-row">
            <td className="py-3 pr-2 font-semibold uppercase tracking-widest text-[#64748b] sticky left-0 bg-white">Total</td>
            {monthlyTotals.map((v, i) => (
              <td key={i} className={`py-3 px-1 text-right font-semibold ${v > 0 ? 'text-[#0f172a]' : 'text-[#cbd5e1]'}`}>
                {v > 0 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
              </td>
            ))}
            <td className="py-3 pl-2 text-right text-base font-semibold text-[#ef4444]">{formatLKR(yearTotal)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

// ---------- Aging Analysis (0-30 / 31-60 / 61-90 / 90+) ----------
const AGING_BUCKETS = [
  { key: 'not_due',   label: 'Not due',   color: '#94a3b8', text: '#334155' },
  { key: 'd_0_30',    label: '0–30 days', color: '#fef3c7', text: '#92400e' },
  { key: 'd_31_60',   label: '31–60 days', color: '#fed7aa', text: '#9a3412' },
  { key: 'd_61_90',   label: '61–90 days', color: '#fecaca', text: '#991b1b' },
  { key: 'd_90_plus', label: '90+ days',  color: '#fca5a5', text: '#7f1d1d' },
];
const AgingTable = ({ data }) => {
  const rawRows = data?.rows || [];
  const totals = data?.totals || {};
  const isResident = data?.group_by === 'resident';
  // Same defensive filter as YearlyMatrixTable: guard against a tab-switch
  // race where `data` is briefly the previous report (yearly matrix rows
  // have `months[]` and no `buckets`, etc.) before the new fetch resolves.
  const rows = rawRows.filter(r => r && typeof r.buckets === 'object' && r.buckets !== null);
  if (rows.length === 0) {
    return <Card className="p-8 text-center bg-white border border-[#e2e8f0]" data-testid="aging-empty">
      <p className="text-sm text-[#64748b]">No outstanding balances anywhere. 🎉</p>
    </Card>;
  }
  return (
    <div className="space-y-4">
      {/* Bucket summary chips */}
      <Card className="p-4 bg-white border border-[#e2e8f0]" data-testid="aging-summary">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {AGING_BUCKETS.map((b) => {
            const val = totals[b.key]?.outstanding || 0;
            const cnt = totals[b.key]?.count || 0;
            return (
              <div
                key={b.key}
                className="rounded-md p-3"
                style={{ background: b.color }}
                data-testid={`aging-bucket-${b.key}`}
              >
                <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: b.text }}>
                  {b.label}
                </p>
                <p className="text-lg font-semibold mt-1" style={{ fontFamily: 'Outfit', color: b.text }}>
                  {formatLKR(val)}
                </p>
                <p className="text-[11px]" style={{ color: b.text }}>
                  {cnt} invoice{cnt === 1 ? '' : 's'}
                </p>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-[#64748b]">
          Grand total: <strong className="text-[#ef4444]">{formatLKR(data?.grand_total)}</strong>
          {' · '}Sorted by oldest debt first
        </p>
      </Card>

      <Card className="p-4 bg-white border border-[#e2e8f0] overflow-x-auto" data-testid="aging-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-[#e2e8f0]">
              <th className="py-2 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                {isResident ? 'Resident' : 'Property'}
              </th>
              {AGING_BUCKETS.map((b) => (
                <th key={b.key} className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">
                  {b.label}
                </th>
              ))}
              <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Total</th>
              <th className="py-2 pl-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right whitespace-nowrap">Oldest</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const rowKey = isResident ? (r.resident_id || r.resident_name) : (r.property_id || r.property_name);
              return (
                <tr key={rowKey} className="border-b border-[#f1f5f9]" data-testid={`aging-row-${rowKey}`}>
                  <td className="py-2 pr-2">
                    <p className="font-medium text-[#0f172a]">
                      {isResident ? r.resident_name : r.property_name}
                    </p>
                    <p className="text-xs text-[#64748b]">
                      {isResident
                        ? `${r.unit_number ? `Unit ${r.unit_number} · ` : ''}${r.property_name} · ${r.invoice_count} inv`
                        : `${r.invoice_count} invoice${r.invoice_count === 1 ? '' : 's'}`}
                    </p>
                  </td>
                  {AGING_BUCKETS.map((b) => {
                    const v = r.buckets[b.key]?.outstanding || 0;
                    const c = r.buckets[b.key]?.count || 0;
                    return (
                      <td key={b.key} className="py-2 px-2 text-right">
                        {v > 0 ? (
                          <>
                            <p className={`text-sm font-medium ${b.key === 'd_90_plus' ? 'text-[#b91c1c]' : b.key === 'd_61_90' ? 'text-[#c2410c]' : 'text-[#0f172a]'}`}>
                              {formatLKR(v)}
                            </p>
                            <p className="text-[11px] text-[#94a3b8]">{c} inv</p>
                          </>
                        ) : <span className="text-[#cbd5e1]">—</span>}
                      </td>
                    );
                  })}
                  <td className="py-2 px-2 text-right font-semibold text-[#ef4444]">{formatLKR(r.total_outstanding)}</td>
                  <td className="py-2 pl-2 text-right text-xs">
                    <span className={
                      r.oldest_days_overdue > 90 ? 'text-[#b91c1c] font-semibold' :
                      r.oldest_days_overdue > 60 ? 'text-[#c2410c] font-semibold' :
                      r.oldest_days_overdue > 30 ? 'text-[#92400e]' :
                      r.oldest_days_overdue > 0  ? 'text-[#64748b]' :
                      'text-[#94a3b8]'
                    }>
                      {r.oldest_days_overdue > 0 ? `${r.oldest_days_overdue}d` : '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-[#e2e8f0]" data-testid="aging-total-row">
              <td className="py-3 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Total</td>
              {AGING_BUCKETS.map((b) => (
                <td key={b.key} className="py-3 px-2 text-right font-semibold">
                  {formatLKR(totals[b.key]?.outstanding || 0)}
                </td>
              ))}
              <td className="py-3 px-2 text-right text-base font-semibold text-[#ef4444]">
                {formatLKR(data?.grand_total)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </Card>
    </div>
  );
};

// ---------- Payment Register (monthly ledger of approved payments) ----------
const MethodChip = ({ label }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#eef2ff] text-[#4338ca]">
    {label || '—'}
  </span>
);

const PaymentRegisterTable = ({ data, rows, periodLabel, onOpenReceipt, hasQuery }) => {
  const backendRows = data?.rows || [];
  const filteredCount = rows.length;
  const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  // Aggregate `by_method` for the filtered set so the summary reflects what
  // the user is looking at, not the raw month total.
  const methodSummary = React.useMemo(() => {
    const acc = {};
    rows.forEach((r) => {
      const key = r.method || 'other';
      const label = r.method_label || key;
      acc[key] = acc[key] || { method: key, method_label: label, count: 0, amount: 0 };
      acc[key].count += 1;
      acc[key].amount += Number(r.amount || 0);
    });
    return Object.values(acc).sort((a, b) => b.amount - a.amount);
  }, [rows]);

  if (backendRows.length === 0) {
    return <Card className="p-8 text-center bg-white border border-[#e2e8f0]" data-testid="payment-register-empty">
      <p className="text-sm text-[#64748b]">No approved payments recorded for {periodLabel}.</p>
    </Card>;
  }

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <Card className="p-4 bg-white border border-[#e2e8f0]" data-testid="payment-register-summary">
        <div className="flex flex-wrap items-baseline gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#64748b]">Total collected</p>
            <p className="text-2xl font-semibold text-emerald-700 mt-1" style={{ fontFamily: 'Outfit' }}>
              {`LKR ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </p>
            <p className="text-[11px] text-[#94a3b8] mt-1">
              {filteredCount} payment{filteredCount === 1 ? '' : 's'}
              {hasQuery ? ` (filtered from ${backendRows.length})` : ''}
            </p>
          </div>
          <div className="flex-1 min-w-[200px] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {methodSummary.map((m) => (
              <div key={m.method} className="rounded-md border border-[#e2e8f0] px-3 py-2" data-testid={`pr-method-${m.method}`}>
                <p className="text-[10px] uppercase tracking-widest text-[#64748b] font-semibold">{m.method_label}</p>
                <p className="text-sm font-semibold text-[#0f172a] mt-0.5">
                  {`LKR ${m.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </p>
                <p className="text-[10px] text-[#94a3b8]">{m.count} pmt{m.count === 1 ? '' : 's'}</p>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Ledger */}
      {filteredCount === 0 ? (
        <Card className="p-8 text-center bg-white border border-[#e2e8f0]" data-testid="payment-register-empty-filtered">
          <p className="text-sm text-[#64748b]">No payments match your search.</p>
        </Card>
      ) : (
        <Card className="p-4 bg-white border border-[#e2e8f0] overflow-x-auto" data-testid="payment-register-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#e2e8f0]">
                <th className="py-2 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] whitespace-nowrap">Date</th>
                <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] whitespace-nowrap">Receipt #</th>
                <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Resident</th>
                <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Method</th>
                <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Applied to</th>
                <th className="py-2 pl-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Amount</th>
                <th className="py-2 pl-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right w-16"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.submission_id} className="border-b border-[#f1f5f9] align-top" data-testid={`pr-row-${r.submission_id}`}>
                  <td className="py-2 pr-2 whitespace-nowrap text-[#0f172a] text-xs">
                    {r.payment_date ? formatDateTime(r.payment_date) : '—'}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    <span className="font-mono text-xs text-[#0f172a]">{r.receipt_number || '—'}</span>
                  </td>
                  <td className="py-2 px-2">
                    <p className="font-medium text-[#0f172a]">{r.resident_name || '—'}</p>
                    <p className="text-[11px] text-[#64748b]">
                      {r.unit_number ? `Unit ${r.unit_number} · ` : ''}{r.property_name || '—'}
                    </p>
                  </td>
                  <td className="py-2 px-2">
                    <MethodChip label={r.method_label} />
                    {r.note && <p className="mt-1 text-[11px] italic text-[#94a3b8] max-w-[160px] truncate" title={r.note}>{r.note}</p>}
                  </td>
                  <td className="py-2 px-2 text-xs">
                    {(r.applied_summary || []).length === 0 ? (
                      <span className="text-[#cbd5e1]">—</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {r.applied_summary.map((a, idx) => (
                          <li key={`${a.invoice_number || 'noinv'}-${idx}`} className="text-[#0f172a]">
                            <span className="font-mono text-[11px] text-[#4338ca]">{a.invoice_number || '—'}</span>
                            <span className="text-[11px] text-[#64748b]"> · {a.label || a.invoice_type}</span>
                            <span className="text-[11px] font-semibold text-[#0f172a]"> · {formatLKR(a.amount_applied)}</span>
                            {!a.fully_paid && <span className="text-[10px] text-red-600 ml-1">(partial)</span>}
                          </li>
                        ))}
                        {r.cascade_overpayment > 0.005 && (
                          <li className="text-[11px] text-amber-700 italic">
                            +{formatLKR(r.cascade_overpayment)} banked as credit
                          </li>
                        )}
                      </ul>
                    )}
                  </td>
                  <td className="py-2 pl-2 text-right whitespace-nowrap font-semibold text-emerald-700">
                    {formatLKR(r.amount)}
                  </td>
                  <td className="py-2 pl-2 text-right">
                    <button
                      type="button"
                      onClick={() => onOpenReceipt(r.submission_id)}
                      className="inline-flex items-center gap-1 text-xs text-[#2563eb] hover:underline"
                      data-testid={`pr-open-receipt-${r.submission_id}`}
                      aria-label={`Open receipt ${r.receipt_number || ''}`}
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#e2e8f0]" data-testid="pr-total-row">
                <td colSpan={5} className="py-3 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">
                  Total collected
                </td>
                <td className="py-3 pl-2 text-right text-base font-semibold text-emerald-700">
                  {`LKR ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// ---------- Existing Monthly Billing (preserved) ----------
const StatusPill = ({ status }) => {
  if (!status) return <span className="text-xs text-[#94a3b8]">—</span>;
  const isPaid = status === 'paid';
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase tracking-wide ${
      isPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
    }`}>{status}</span>
  );
};

const MonthlyBillingTable = ({ data }) => {
  const rows = data?.rows || [];
  const t = data?.totals || {};
  if (rows.length === 0) {
    return <Card className="p-8 text-center bg-white border border-[#e2e8f0]">
      <p className="text-sm text-[#64748b]">No invoices for this period.</p>
    </Card>;
  }
  return (
    <Card className="p-4 bg-white border border-[#e2e8f0] overflow-x-auto" data-testid="monthly-billing-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[#e2e8f0]">
            <th className="py-2 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Resident</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Maint. Billed</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Maint. Paid</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Status</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Water Billed</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Water Paid</th>
            <th className="py-2 pl-2 text-xs font-semibold uppercase tracking-widest text-[#64748b] text-right">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.resident_id} className="border-b border-[#f1f5f9]">
              <td className="py-2 pr-2">
                <p className="font-medium text-[#0f172a]">{r.resident_name}</p>
                <p className="text-xs text-[#64748b]">{r.unit_number ? `Unit ${r.unit_number} · ` : ''}{r.property_name}</p>
              </td>
              <td className="py-2 px-2 text-right">{formatLKR(r.maintenance_amount)}</td>
              <td className="py-2 px-2 text-right text-green-700">{formatLKR(r.maintenance_paid)}</td>
              <td className="py-2 px-2"><StatusPill status={r.maintenance_status} /></td>
              <td className="py-2 px-2 text-right">{formatLKR(r.water_amount)}</td>
              <td className="py-2 px-2 text-right text-green-700">{formatLKR(r.water_paid)}</td>
              <td className="py-2 pl-2 text-right font-semibold text-[#ef4444]">{formatLKR(r.total_outstanding)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-[#e2e8f0]">
            <td className="py-3 pr-2 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Totals</td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.maintenance_billed)}</td>
            <td className="py-3 px-2 text-right font-semibold text-green-700">{formatLKR(t.maintenance_collected)}</td>
            <td></td>
            <td className="py-3 px-2 text-right font-semibold">{formatLKR(t.water_billed)}</td>
            <td className="py-3 px-2 text-right font-semibold text-green-700">{formatLKR(t.water_collected)}</td>
            <td className="py-3 pl-2 text-right text-base font-semibold text-[#ef4444]">{formatLKR(t.total_outstanding)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

export default Reports;
